// @flow
import React, { useState, useEffect, useRef } from 'react'
import cx from 'classnames'
import { isBoolean } from 'lodash'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Popper from '@material-ui/core/Popper'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import type { Option, InputProps } from 'app/core/types'
import FontIcon from 'app/components/FontIcon/FontIcon.jsx'
import { getColorFromBackground } from 'app/libs/helpers/getColorFromBackground'
import vars from 'app/styles/vars.js'
import { colors } from 'app/styles/colors.js'
import LoaderSmall from 'app/components/LoaderSmall/LoaderSmall.jsx'
import { cyLabelFormater } from 'app/libs/helpers/cyTools.js'

import Validators from '../Validators/Validators.jsx'

const { tableFont } = vars

let timer: TimeoutID | void
export type AutocompleteProps = {|
  autoComplete?: boolean, // If true, the portion of the selected suggestion that has not been typed by the user, known as the completion string, appears inline after the input cursor in the textbox. The inline completion string is visually highlighted and has a selected state.
  autoHighlight?: boolean,
  autoSelect?: boolean, // If true, the selected option becomes the value of the input when the Autocomplete loses focus unless the user chooses a different option or changes the character string in the input.
  blurOnSelect?: 'mouse' | 'touch' | boolean,
  ChipProps?: Object, // Props applied to the Chip element.
  classes?: Object, // Override or extend the styles applied to the component. See CSS API below for more details.
  clearOnBlur?: boolean, // !props.freeSolo	If true, the input's text will be cleared on blur if no value is selected.
  clearOnEscape?: boolean, // false	If true, clear all values when the user presses escape and the popup is closed.
  clearOnFocus?: boolean,
  clearText?: string, // 'Clear'	Override the default text for the clear icon button.
  clearOnValidate?: boolean,
  closeIcon?: React$Element<any> | null, // <CloseIcon fontSize="small" />	The icon to display in place of the default close icon.
  closeText?: string, // 'Close'	Override the default text for the close popup icon button.
  debug?: boolean, // false	If true, the popup will ignore the blur event if the input is filled. You can inspect the popup markup with your browser tools. Consider this option when you need to customize the component.
  defaultValue?: any, // props.multiple ? [] : null	The default input value. Use when the component is not controlled.
  disableClearable?: boolean, // false	If true, the input can't be cleared.
  disableCloseOnSelect?: boolean, // false	If true, the popup won't close when a value is selected.
  disabled?: boolean, // false	If true, the input will be disabled.
  disabledItemsFocusable?: boolean, // false	If true, will allow focus on disabled items.
  disableListWrap?: boolean, // false	If true, the list box in the popup will not wrap focus.
  disablePortal?: boolean, // false	Disable the portal behavior. The children stay within it's parent DOM hierarchy.
  filterOptions?: (options: Array<Option>, state: Object) => void, // A filter function that determines the options that are eligible.
  filterSelectedOptions?: boolean, // false	If true, hide the selected options from the list box.
  forcePopupIcon?: 'auto' | boolean, // 'auto'	Force the visibility display of the popup icon.
  freeSolo?: boolean, // false	If true, the Autocomplete is free solo, meaning that the user input is not bound to provided options.
  fullWidth?: boolean, // false	If true, the input will take up the full width of its container.
  getLimitTagsText?: (more: number) => React$Element<any> | string, // 	(more) => +${more}	The label to display when the tags are truncated (limitTags).
  getOptionDisabled?: (option: Option) => boolean, // Used to determine the disabled state for a given option.
  getOptionLabel?: (option: Option) => string, // (x) => x	Used to determine the string value for a given option. It's used to fill the input (and the list box options if renderOption is not provided).
  getOptionSelected?: (option: Option, value: any) => boolean, // 		Used to determine if an option is selected, considering the current value. Uses strict equality by default.
  groupBy?: (options: Option) => string, // If provided, the options will be grouped under the returned string. The groupBy value is also used as the text for group headings when renderGroup is not provided.
  handleHomeEndKeys?: boolean, // !props.freeSolo	If true, the component handles the "Home" and "End" keys when the popup is open. It should move focus to the first option and last option, respectively.
  id?: string, // This prop is used to help implement the accessibility logic. If you don't provide this prop. It falls back to a randomly generated id.
  includeInputInList?: boolean, // false	If true, the highlight can move to the input.
  inputValue?: string, // The input value.
  limitTags?: number, // -1	The maximum number of tags that will be visible when not focused. Set -1 to disable the limit.
  ListboxComponent?: (params: Object) => React$Element<any> | string, // 'ul'	The component used to render the listbox.
  ListboxProps?: Object, // Props applied to the Listbox element.
  loading?: boolean, // false	If true, the component is in a loading state.
  loadingText?: React$Element<any>, // 'Loading…'	Text to display when in a loading state.
  multiple?: boolean, // false	If true, value must be an array and the menu will support multiple selections.
  noOptionsText?: React$Element<any>, // 'No options'	Text to display when there are no options.
  onClose?: (event: Object, reason: string) => void, // Callback fired when the popup requests to be closed. Use in controlled mode (see open).
  onHighlightChange?: (event: Object, option: Option, reason: string) => void, // Callback fired when the highlight option changes.
  onInputChange?: (event: Object, value: string, reason: string) => void, // Callback fired when the input value changes.
  onOpen?: (event: Object) => void, // Callback fired when the popup requests to be opened. Use in controlled mode (see open).
  open?: boolean, // Control the popup` open state.
  openOnFocus?: boolean, // false	If true, the popup will open on input focus.
  openText?: string, // 'Open'	Override the default text for the open popup icon button.
  PaperComponent?: (params: Object) => React$Element<any> | string, // Paper	The component used to render the body of the popup.
  PopperComponent?: (params: Object) => React$Element<any> | string, // Popper	The component used to position the popup.
  popupIcon?: React$Element<any>, // <ArrowDropDownIcon />	The icon to display in place of the default popup icon.
  renderGroup?: (params: Object) => React$Element<any>, // Render the group.
  renderInput?: (params: Object) => React$Element<any>, // Render the input.
  renderOption?: (params: Object) => React$Element<any>, // Render the option, use getOptionLabel by default.
  renderTags?: (options: Array<Option>, getTagProps: Function) => React$Element<any> | Array<React$Element<any>>, // Render the selected value.
  selectOnFocus?: boolean, // !props.freeSolo	If true, the input's text will be selected on focus. It helps the user clear the selected value.
  size?: 'medium' | 'small', // 'medium'	The size of the autocomplete.
  options?: Array<Option>,
  value?: any,

  // ADDED PROPS
  ...InputProps,
  onChange?: (option: any) => any, // Callback fired when the value changes.
  disabledOptions?: Array<string>,
  inputRef?: React$ElementRef<any>,
  onSearch?: (inputValue: string) => Promise<any>,
  autoFocus?: boolean,
  additionnalOptions?: Array<Option>,
  defaultOpenOptions?: boolean,
  inputProps?: Object,
  renderLabel?: (opt: Option) => React$Element<any>,
  dataCy?: string | React$Node,
  customItemLabel?: (option: Option) => React$Node,
  subLabel?: React$Node,
  startAdornment?: React$Node,
  endAdornment?: React$Node,
  noStyle?: boolean,

  // Validators
  validatorRef?: Function,
  validatorStyle?: Object,
  errors?: Object,
  isRequired?: boolean,
  validators?: Object,
|}

const useStyles: (Object) => { [key: string]: string } = makeStyles(() => ({
  option: {
    padding: 0,
    fontFamily: tableFont,
    backgroundColor: 'transparent !important',
    '&[data-focus="true"]>div': { boxShadow: '0 0 0 30px rgba(0,0,0,0.1) inset' },
    '&:hover>div': { boxShadow: '0 0 0 30px rgba(0,0,0,0.1) inset' },
  },
  listbox: { padding: 0 },
  root: { width: '100%' },
  textFieldRoot: ({ noStyle, multiple }) => ({
    borderRadius: noStyle ? undefined : 4,
    border: noStyle ? undefined : '1px solid #B3B9c7',
    padding: multiple ? '5px 8px' : '0 8px',
  }),
  textFieldError: {
    borderColor: colors.red,
    boxShadow: `0 0 3px ${colors.red}`,
  },
  textFieldDisabled: {
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  input: ({ multiple }) => ({ padding: '4px 0px !important', minWidth: multiple ? '100px !important' : undefined }),
}))

export function MUIAutocompleteMultiple(props: AutocompleteProps): React$Node {
  const {
    disabledOptions,
    options,
    onChange,
    onBlur,
    onFocus,
    inputRef,
    openOnFocus,
    defaultOpenOptions,
    onSearch,
    closeIcon,
    open,
    multiple,
    placeholder,
    autoFocus,
    validatorRef,
    validatorStyle,
    errors,
    clearOnEscape = true,
    isRequired = false,
    validators,
    value,
    additionnalOptions,
    inputProps: userInputProps,
    startAdornment: _startAdornment,
    endAdornment: _endAdornment,
    disabled,
    renderLabel,
    clearOnValidate = true,
    defaultValue,
    clearOnFocus,
    dataCy,
    customItemLabel,
    subLabel,
    renderTags,
    noStyle,
    filterOptions,
    ...rest
  } = props
  const [_options, setOptions] = useState(options || [])
  const [isSearching, setIsSearching] = useState(false)
  const [focus, setFocus] = useState(false)
  const [error, setError] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const isMounted = useRef()
  const _inputRef = useRef()

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    let sortedOptions = options || []
    if (additionnalOptions) sortedOptions = sortedOptions.concat(additionnalOptions)
    if (disabledOptions) sortedOptions = sortedOptions.filter((opt) => !disabledOptions.includes(opt.value))
    sortedOptions = sortedOptions.filter((opt) => !value?.find((vals) => vals.value === opt.value))

    setOptions(sortedOptions)
  }, [options])

  function _onChange(event: SyntheticInputEvent<>, value: Option) {
    if (Array.isArray(value) && onChange) onChange(value.filter((opt) => typeof opt === 'object'))
    else if (typeof value === 'object' && onChange) onChange(value)
    setOptions([])
    if (clearOnValidate) setInputValue('')
  }

  const _onInputChange = (event: SyntheticInputEvent<>, inputText: string) => {
    if (onSearch && (!inputText || inputText.length === 0)) {
      if (isMounted.current) setOptions([])
      return
    }
    if (onSearch && inputText && inputText.length > 0) {
      if (isMounted.current) setIsSearching(true)

      clearTimeout(timer)
      timer = setTimeout(() => {
        onSearch(inputText).then((results) => {
          if (isMounted.current) {
            setOptions(results)
            setIsSearching(false)
          }
        })
      }, 500)
    }
  }

  function _renderOption(option: Option) {
    const { label, backgroundColor, icon, value, labelCustom } = option
    if (labelCustom) {
      return (
        <div
          className="flex row noWrap alignCenter fullWidth"
          style={{
            backgroundColor,
            padding: '5px 10px',
            color: backgroundColor && getColorFromBackground(backgroundColor),
          }}
          data-testid={cyLabelFormater('autocomplete-choice', value || customItemLabel?.(option) || label)}
        >
          {labelCustom}
        </div>
      )
    }

    return (
      <div
        className="flex row noWrap alignCenter fullWidth"
        style={{
          backgroundColor,
          padding: '3px 6px',
          color: backgroundColor && getColorFromBackground(backgroundColor),
        }}
        data-testid={cyLabelFormater('autocomplete-choice', value || customItemLabel?.(option) || label)}
      >
        {icon ? <FontIcon icon={icon} className="marginRight5" /> : null}
        {customItemLabel?.(option) || label}
      </div>
    )
  }

  function getInputRef(ref: React$ElementRef<any>) {
    if (autoFocus && !_inputRef.current) {
      ref?.focus?.()
      _inputRef.current = ref
    }

    if (inputRef) {
      if (typeof inputRef === 'function') inputRef(ref)
      else if (typeof inputRef === 'object') inputRef.current = ref
    }
  }

  function _renderInput(InputParams: Object) {
    const { InputProps, ...inputParams } = InputParams

    const { textFieldError, textFieldDisabled, textFieldRoot } = useStyles({ multiple, noStyle })
    const { inputProps, ...restInputParams } = inputParams
    const { inputProps: userinputProps, ...userInputRest } = userInputProps || {}

    const _onChangeInput = (e) => {
      if (e.target.value) inputProps.onChange(e)
      setInputValue(e.target.value)
      _onInputChange(e, e.target.value)
    }

    return (
      <TextField
        InputProps={{
          ...InputProps,
          ...userInputRest,
          disableUnderline: true,
          startAdornment: InputProps.startAdornment || _startAdornment,
          endAdornment: isSearching ? (
            <LoaderSmall />
          ) : !noStyle ? (
            <FontIcon icon="fas-search" className={focus ? undefined : 'grey'} />
          ) : null,
          inputProps: {
            ...inputProps,
            ...userinputProps,
            value: inputValue,
            onChange: _onChangeInput,
            'data-testid': cyLabelFormater('input', dataCy || placeholder),
          },
        }}
        classes={{
          root: cx(textFieldRoot, {
            [textFieldError]: error,
            [textFieldDisabled]: disabled,
          }),
        }}
        {...restInputParams}
        inputRef={(ref) => {
          if (getInputRef.current) getInputRef.current = ref
        }}
        placeholder={placeholder}
      />
    )
  }

  function _getOptionSelected(option: Option, selectedValue: ?mixed) {
    if (!selectedValue) return true
    if (!option) return false

    if (typeof selectedValue === 'string' && selectedValue === '') {
      return null
    }
    if (typeof selectedValue === 'object') {
      return option.value === selectedValue
    }
    if (['string', 'number'].includes(typeof selectedValue)) {
      return option.value === selectedValue
    }

    return false
  }

  function _filterOptions(options: Array<Option>) {
    if (!Array.isArray(value) || !value) return options
    return options.filter((opt) => !value.find((o) => o.value === opt.value))
  }

  const { option, listbox, root, input } = useStyles({ multiple, noStyle })

  return (
    <div className="relative fullWidth">
      <Autocomplete
        autoComplete={true}
        forcePopupIcon={false}
        clearOnEscape={clearOnEscape}
        disableClearable={true}
        autoSelect={false}
        clearOnBlur={false}
        freeSolo={true}
        filterOptions={filterOptions || _filterOptions}
        openOnFocus={openOnFocus || defaultOpenOptions}
        renderInput={_renderInput}
        fullWidth={true}
        classes={{ option, listbox, root, input }}
        inputValue={inputValue}
        multiple={true}
        data-testid="autocomplete"
        PopperComponent={(popperProps: Object) => {
          const { style, ...popperPropsRest } = popperProps
          return <Popper style={{ ...style, width: undefined }} {...popperPropsRest} />
        }}
        noOptionsText="No results"
        onChange={_onChange}
        open={isBoolean(open) ? open : focus && _options.length > 0}
        disabled={disabled}
        loading={isSearching}
        onFocus={(event: SyntheticFocusEvent<>) => {
          if (onFocus) onFocus(event)
          setFocus(true)
        }}
        onBlur={(event: SyntheticFocusEvent<>) => {
          if (onBlur) onBlur(event)
          if (clearOnFocus && !inputValue) {
            if (typeof value === 'string') setInputValue(value)
            else if (typeof value === 'object') setInputValue(value?.label)
          }
          setFocus(false)
        }}
        {...rest}
        options={_options}
        renderOption={_renderOption}
        getOptionLabel={(option: Option) => option.label || ''}
        getOptionSelected={_getOptionSelected}
        value={value || []}
        defaultValue={defaultValue}
        renderTags={renderTags}
      />
      {subLabel}
      <Validators
        ref={(ref) => {
          if (validatorRef) validatorRef(ref)
        }}
        style={{ ...validatorStyle, position: 'absolute', right: 0 }}
        value={value || ''}
        errors={errors}
        onSuccess={() => setError(false)}
        onError={() => setError(true)}
        validators={{
          required: isRequired,
          ...validators,
        }}
      />
    </div>
  )
}
